// =============================================================
// WAVE 4 — User groups (RBAC group CRUD).
//
// Ports the prototype SecUserGroups over the REAL /user-groups. Lists groups
// with a member count (derived from users' groupIds, cheap), a compact summary
// of each group's permission levels, and create / edit (name) / delete CRUD.
// Delete is confirmed via useChrome confirm. Write affordances hide without
// admin:write (the server is still authoritative).
//
// HONEST API NOTES — the live `user_groups` schema (apps/server/src/db/schema.ts)
// carries only { name, permissions }. So, relative to the prototype mock:
//   - Description field → OMITTED. The schema has no `description` column; we do
//     not POST/PATCH a field the server would drop.
//   - "Default group" star → OMITTED. There is NO set-default endpoint and the
//     schema has no isDefault/default field, so we do not fake a default toggle.
//   - The editable R/W/D permission MATRIX lives in People & access (a later
//     wave's focus); here Groups stays focused on group CRUD and shows the
//     permission levels READ-ONLY (derived from permissions.levels via the core
//     levelName ladder — the same source of truth the server enforces).
// =============================================================

import { useState } from "react";
import {
  PERM_MODULES,
  levelName,
  type AccessLevel,
  type GroupPermissions,
  type PermModule,
} from "@perpetuum/core";
import { useAuth } from "../app/AuthContext.js";
import { useChrome } from "../app/ChromeContext.js";
import {
  useCollection,
  useCreateRow,
  useDeleteRow,
  useUpdateRow,
  type Row,
} from "../lib/queries.js";
import { useScopedT } from "../lib/i18n-keys.js";
import { Btn, Icon, ICONS } from "../components/primitives.js";
import { EmptyState } from "../components/EmptyState.js";
import { Skeleton } from "../components/Skeleton.js";

interface GroupRow extends Row {
  permissions?: GroupPermissions;
}

const MODULE_LABEL: Record<PermModule, string> = {
  dev: "Devices",
  tasks: "Tasks",
  spares: "Spares",
  downtime: "Downtime",
  reports: "Reports",
  admin: "Admin",
};

export function Groups() {
  const t = useScopedT();
  const { can } = useAuth();
  const { toast, confirm } = useChrome();
  const canWrite = can("admin", "write");

  const groupsQ = useCollection<GroupRow>("user-groups");
  const usersQ = useCollection("users");
  const create = useCreateRow("user-groups");
  const update = useUpdateRow("user-groups");
  const remove = useDeleteRow("user-groups");

  const groups = groupsQ.data ?? [];
  const users = usersQ.data ?? [];

  // Member count derived from users' groupIds (cheap; no extra endpoint).
  const memberCount = (groupId: string): number =>
    users.filter((u) => {
      const ids = (u as { groupIds?: string[] }).groupIds;
      return Array.isArray(ids) && ids.includes(groupId);
    }).length;

  // Compact permission summary: modules at View+ with their level name.
  const permSummary = (perms: GroupPermissions | undefined): { mod: PermModule; label: string; level: string }[] => {
    if (!perms?.levels) return [];
    return PERM_MODULES.filter((m) => (perms.levels[m] ?? 0) >= 1).map((m) => ({
      mod: m,
      label: MODULE_LABEL[m],
      level: levelName((perms.levels[m] ?? 0) as AccessLevel, m),
    }));
  };

  // Create / edit form state. `editId === null` ⇒ create mode.
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [name, setName] = useState("");

  const openCreate = () => {
    setEditId(null);
    setName("");
    setFormOpen(true);
  };
  const openEdit = (g: GroupRow) => {
    setEditId(g.id);
    setName(String(g.name ?? ""));
    setFormOpen(true);
  };
  const closeForm = () => {
    setFormOpen(false);
    setEditId(null);
    setName("");
  };

  const onSubmit = async () => {
    if (!name.trim()) return;
    try {
      if (editId) {
        await update.mutateAsync({ id: editId, body: { name: name.trim() } });
        toast(t("groups.updated", { name: name.trim() }), "success");
      } else {
        // The server seeds an empty permission matrix; editing it is the People
        // & access matrix's job. We only create the named group here.
        await create.mutateAsync({ name: name.trim(), permissions: { levels: {}, visibleCardTabs: [] } });
        toast(t("groups.created", { name: name.trim() }), "success");
      }
      closeForm();
    } catch {
      toast(t("groups.saveErr"), "error");
    }
  };

  const onDelete = async (g: GroupRow) => {
    const ok = await confirm({
      title: t("groups.deleteTitle"),
      body: t("groups.deleteConfirm", { name: String(g.name) }),
      confirmLabel: t("groups.delete"),
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(g.id);
      toast(t("groups.deleted", { name: String(g.name) }), "info");
    } catch {
      toast(t("groups.deleteErr"), "error");
    }
  };

  return (
    <div>
      <h2 className="d-h2">{t("groups.title")}</h2>
      <p className="d-sub">{t("groups.sub")}</p>

      <div className="d-card">
        <div className="d-card-hd">
          <h3>{t("groups.groups")}</h3>
          <span className="sp" style={{ flex: 1 }} />
          {canWrite && (
            <Btn variant="secondary" size="sm" data-testid="grp-new" onClick={openCreate}>
              <Icon d={ICONS.plus} size={16} />
              {t("groups.new")}
            </Btn>
          )}
        </div>
        <div className="d-card-body" style={{ paddingTop: 4, paddingBottom: 4 }}>
          {groupsQ.isLoading && (
            <div style={{ padding: "8px 0" }}>
              <Skeleton variant="text" count={4} />
            </div>
          )}

          {!groupsQ.isLoading &&
            groups.map((g) => {
              const summary = permSummary(g.permissions);
              return (
                <div className="grp-row" key={g.id} data-testid={`grp-row-${g.id}`}>
                  <div className="gi">
                    <Icon d={ICONS.users} size={20} />
                  </div>
                  <div className="gn">
                    <div className="nm" data-testid={`grp-name-${g.id}`}>
                      {String(g.name)}
                    </div>
                    <div className="ds" data-testid={`grp-perms-${g.id}`}>
                      {summary.length > 0 ? (
                        <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 6 }}>
                          {summary.map((s) => (
                            <span
                              key={s.mod}
                              className="chip-status info"
                              style={{ fontSize: 11 }}
                              title={`${s.label}: ${s.level}`}
                            >
                              <span className="led" />
                              {s.label} · {s.level}
                            </span>
                          ))}
                        </span>
                      ) : (
                        t("groups.noPerms")
                      )}
                    </div>
                  </div>
                  <div className="gcount" data-testid={`grp-count-${g.id}`}>
                    {t("groups.members", { n: memberCount(g.id) })}
                  </div>
                  {canWrite && (
                    <span style={{ display: "inline-flex", gap: 6 }}>
                      <Btn variant="ghost" size="sm" data-testid={`grp-edit-${g.id}`} onClick={() => openEdit(g)}>
                        {t("groups.edit")}
                      </Btn>
                      <Btn variant="ghost" size="sm" data-testid={`grp-delete-${g.id}`} onClick={() => void onDelete(g)}>
                        <Icon d={ICONS.trash} size={15} />
                      </Btn>
                    </span>
                  )}
                </div>
              );
            })}

          {!groupsQ.isLoading && groups.length === 0 && (
            <EmptyState
              icon={ICONS.users}
              title={t("groups.empty")}
              hint={t("groups.emptyHint")}
              action={canWrite ? { label: t("groups.new"), onClick: openCreate } : undefined}
              compact
            />
          )}
        </div>
      </div>

      {formOpen && canWrite && (
        <div className="modal-scrim" onClick={closeForm}>
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ width: 460 }}
            role="dialog"
            aria-modal="true"
            aria-label={editId ? t("groups.editTitle") : t("groups.newTitle")}
            data-testid="grp-form"
            onKeyDown={(e) => {
              if (e.key === "Escape") closeForm();
            }}
          >
            <div className="modal-hd">
              <div className="ic" style={{ background: "var(--brand-wash)", color: "var(--brand-primary)" }}>
                <Icon d={ICONS.shield} size={20} />
              </div>
              <h3>{editId ? t("groups.editTitle") : t("groups.newTitle")}</h3>
            </div>
            <div className="modal-body">
              <div className="d-field">
                <label htmlFor="grp-name">{t("groups.field.name")}</label>
                <div className="field-ctrl">
                  <input
                    id="grp-name"
                    data-testid="grp-name-input"
                    autoFocus
                    placeholder={t("groups.field.namePlaceholder")}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void onSubmit();
                    }}
                  />
                </div>
              </div>
              <div className="note" style={{ marginTop: 4 }}>
                <Icon d={ICONS.alert} size={16} />
                <span>{t("groups.matrixNote")}</span>
              </div>
            </div>
            <div className="modal-foot">
              <Btn variant="ghost" size="sm" onClick={closeForm}>
                {t("common.cancel")}
              </Btn>
              <Btn
                variant="primary"
                size="sm"
                data-testid="grp-submit"
                disabled={!name.trim() || create.isPending || update.isPending}
                onClick={() => void onSubmit()}
              >
                {editId ? t("groups.save") : t("groups.create")}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
